import { FC } from "react";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";

import FailedJobNotification from "@/components/Notifications/FailedJobNotification";
import { Notifications } from "@/hooks/optimisation/useFailedJobsNotifications";
import Text from "@/components/Text";
import Button from "@/components/Button";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";

const Wrap = styled.div`
  margin-top: 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  justify-content: space-between;
`;

interface Props {
  notifications?: Notifications;
  queryKey: Array<string | object>;
}

const OptimisationProjectNotifications: FC<Props> = ({
  notifications,
  queryKey,
}) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  if (!notifications?.length) {
    return null;
  }

  const onDeleteAll = async () => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete all failed tests?"
      );

      if (!confirmed) {
        return;
      }

      await Promise.all(
        notifications
          .reduce<number[]>((acc, curr) => {
            if (acc.includes(curr.data.meta.testUnitId)) {
              return acc;
            }
            return [...acc, curr.data.meta.testUnitId];
          }, [])
          .map((id) =>
            api.delete(`/v1/optimisation-test-units/${id}`).catch(() => null)
          )
      );
      await Promise.all(
        notifications.map((notif) =>
          api.put(`/v1/notifications/${notif.id}/read`)
        )
      );
      await queryClient.invalidateQueries(["optimisation_tests"]);
      await queryClient.invalidateQueries(queryKey);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const onRowDelete = async (testUnitId: number) => {
    await api
      .delete(`/v1/optimisation-test-units/${testUnitId}`)
      .catch(() => null);

    await Promise.all(
      notifications
        .filter((notif) => notif.data.meta.testUnitId === testUnitId)
        .map((notif) => api.put(`/v1/notifications/${notif.id}/read`))
    );

    await queryClient.invalidateQueries(["optimisation_tests"]);
    await queryClient.invalidateQueries(queryKey);
  };

  return (
    <Wrap>
      <Header>
        <Text $type="smallBold">
          Errors List{" "}
          <Text $type="smallBold" $color="gray" as="span">
            ({notifications.length} errors appears)
          </Text>
        </Text>
        <Button small $type="colorized" onClick={onDeleteAll}>
          Delete All
        </Button>
      </Header>
      {notifications.map((notification) => (
        <FailedJobNotification
          queryKey={queryKey}
          onRowDelete={() => onRowDelete(notification.data.meta.testUnitId)}
          minimal
          notification={notification}
          retriable
          key={notification.id}
        />
      ))}
    </Wrap>
  );
};

export default OptimisationProjectNotifications;
